import { _ } from "../../core/polyglot/polyglot.js";
import {
	formatAsMonth,
	formatAsMonthAndYear,
} from "../../core/helpers/date-formatting-util.js";
import Period from "./Period.js";

/**
 * `Consecutive months` is a period representing a certain number of consecutive months within a fiscal year.
 *
 * @param {string} [spec.label] - When provided, use the label as display
 * string in `toSmartString`.
 * @param {integer} additionalMonths - Number of months to be added to the periodIndex to calculate the number of total consecutive months.
 * The maximum limit is set to the available months in the fiscal year, the number being truncated if this limit is exceeded.
 */
export default class ConsecutiveMonths extends Period {
	constructor({
		year,
		additionalMonths,
		label,
		periodIndex,
		name = _("Consecutive months"),
		...args
	}) {
		const allMonths = year.asMonths();
		const startMonth = allMonths[periodIndex - 1];

		// We have to keep the ConsecutiveMonths within the fiscalYear.
		// Therefore, we have to ensure the lastIndex is not out-of-range.
		const lastIndex = Math.min(
			periodIndex - 1 + additionalMonths,
			allMonths.length - 1,
		);

		const endMonth = allMonths[lastIndex];

		const start = startMonth.getStart();
		const end = new Date(endMonth.getEnd());
		end.setHours(23, 59, 59, 999);

		super({
			...args,
			year,
			label,
			periodIndex,
			start,
			end,
		});

		this._name = name;
		this._additionalMonths = additionalMonths;
		this._label = label;
		this._startMonth = startMonth;
		this._endMonth = endMonth;
	}

	getAdditionalMonths() {
		return this._additionalMonths;
	}

	setName(name) {
		this._name = name;
	}

	isConsecutiveMonths() {
		return true;
	}

	startMonth() {
		return this._startMonth;
	}

	endMonth() {
		return this._endMonth;
	}

	getLabel() {
		return this._label;
	}

	getEquivalentInFiscalYear(year) {
		return new ConsecutiveMonths({
			year,
			periodIndex: this._periodIndex,
			additionalMonths: this._additionalMonths,
		});
	}

	toString() {
		return `${this.getPeriodType()}-${this.getPeriodIndex()}_${
			this._additionalMonths
		}-${this._year.getName()}`;
	}

	toSmartString() {
		if (this._label) {
			return this._label;
		}

		return `${formatAsMonth(
			this.getStartDateString(),
		)} - ${formatAsMonthAndYear(this.getEndDateString())}`;
	}

	static getType() {
		return "ConsecutiveMonths";
	}

	static transformMaterializationData(data, context) {
		super.transformMaterializationData(...arguments);

		data.year ||= context.getFiscalYearById(data.fiscalYearId);
		data.additionalMonths ||= data.monthData.length - 1;
	}
}
